/**
 * MealTagFilter component
 * Multi-select tag filter dropdown for meals
 */

import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMealTags } from '@/hooks/useMeals';
import { capitalize } from '@/lib/utils/text';

interface MealTagFilterProps {
  selectedTags: string[];
  onChange: (tags: string[]) => void;
}

/**
 * MealTagFilter component
 *
 * Features:
 * - Multi-select dropdown
 * - Shows tag counts
 * - Displays selected tags as badges
 * - Clear all button
 * - Click outside to close
 *
 * @example
 * ```tsx
 * const [tags, setTags] = useState<string[]>([]);
 * <MealTagFilter selectedTags={tags} onChange={setTags} />
 * ```
 */
export function MealTagFilter({ selectedTags, onChange }: MealTagFilterProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { data: tagsData, isLoading } = useMealTags();

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleToggleTag = (tag: string) => {
    if (selectedTags.includes(tag)) {
      onChange(selectedTags.filter((t) => t !== tag));
    } else {
      onChange([...selectedTags, tag]);
    }
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const availableTags = tagsData?.tags || [];

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Dropdown button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="relative w-full rounded-md border-0 py-2 pl-3 pr-10 text-left text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-primary-600 sm:text-sm sm:leading-6 bg-white"
        disabled={isLoading}
      >
        <span className="block truncate">
          {selectedTags.length === 0
            ? t('meals.filterByTags')
            : t('meals.tagsSelected', { count: selectedTags.length })}
        </span>
        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
          <svg
            className="h-5 w-5 text-gray-400"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M10 3a.75.75 0 01.55.24l3.25 3.5a.75.75 0 11-1.1 1.02L10 4.852 7.3 7.76a.75.75 0 01-1.1-1.02l3.25-3.5A.75.75 0 0110 3zm-3.76 9.2a.75.75 0 011.06.04l2.7 2.908 2.7-2.908a.75.75 0 111.1 1.02l-3.25 3.5a.75.75 0 01-1.1 0l-3.25-3.5a.75.75 0 01.04-1.06z"
              clipRule="evenodd"
            />
          </svg>
        </span>
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
          {/* Clear all button */}
          {selectedTags.length > 0 && (
            <button
              type="button"
              onClick={handleClearAll}
              className="w-full text-left px-3 py-2 text-sm text-primary-600 hover:bg-primary-50 border-b border-gray-200"
            >
              {t('meals.clearAllTags')}
            </button>
          )}

          {/* Tag options */}
          {availableTags.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">{t('meals.noTagsAvailable')}</div>
          ) : (
            availableTags.map((tag) => {
              const isSelected = selectedTags.includes(tag.tag);
              return (
                <button
                  key={tag.tag}
                  type="button"
                  onClick={() => handleToggleTag(tag.tag)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex items-center justify-between ${
                    isSelected ? 'bg-primary-50 text-primary-900' : 'text-gray-900'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                    />
                    {capitalize(tag.tag)}
                  </span>
                  <span className="text-xs text-gray-500">({tag.count})</span>
                </button>
              );
            })
          )}
        </div>
      )}

      {/* Selected tags badges */}
      {selectedTags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {selectedTags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-primary-100 text-primary-800"
            >
              {capitalize(tag)}
              <button
                type="button"
                onClick={() => handleToggleTag(tag)}
                className="hover:text-primary-900"
                aria-label={t('meals.removeTagFilter', { tag: capitalize(tag) })}
              >
                <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
